import React, { useEffect, useState } from "react";
import {
  ActionIcon,
  Alert,
  Box,
  FileButton,
  Group,
  Image,
  Loader,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { deleteReq, formatAxiosErrorMessage, get, uploadFile } from "../../_api";
import { MEDIA_BASE } from "../../config";

export interface IMediaItem {
  _id: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  uploadedAt: string;
}

export function mediaUrl(item: Pick<IMediaItem, "filename">): string {
  return `${MEDIA_BASE}/media/${item.filename}`;
}

interface Props {
  onPick?: (url: string, item: IMediaItem) => void;
  selectedUrl?: string;
}

const MediaGrid: React.FC<Props> = ({ onPick, selectedUrl }) => {
  const [items, setItems] = useState<IMediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setLoading(true);
    try {
      const res: IMediaItem[] = await get("media");
      setItems(res ?? []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const handleUpload = async (file: File | null) => {
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      const created: IMediaItem = await uploadFile("media", file);
      setItems((prev) => [created, ...prev]);
    } catch (e) {
      setError(formatAxiosErrorMessage(e));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Usunąć ten plik?")) return;
    try {
      await deleteReq(`media/${id}`);
      setItems((prev) => prev.filter((i) => i._id !== id));
    } catch (e) {
      setError(formatAxiosErrorMessage(e));
    }
  };

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text fw={600}>Biblioteka mediów</Text>
        <FileButton onChange={handleUpload} accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml">
          {(props) => (
            <Box {...props} component="button" type="button" style={{ cursor: "pointer" }}>
              <Text component="span" c="orange" fw={500}>
                {uploading ? "Wgrywanie..." : "+ Wgraj obraz"}
              </Text>
            </Box>
          )}
        </FileButton>
      </Group>

      {error && (
        <Alert color="red" variant="light" withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Group justify="center" p="xl">
          <Loader size="sm" />
        </Group>
      ) : items.length === 0 ? (
        <Text c="dimmed" ta="center" py="xl">
          Brak wgranych plików.
        </Text>
      ) : (
        <Box
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
            gap: 12,
          }}
        >
          {items.map((item) => {
            const url = mediaUrl(item);
            const selected = selectedUrl === url;
            return (
              <Box
                key={item._id}
                style={{
                  position: "relative",
                  border: selected ? "2px solid #FF7F00" : "1px solid #e9ecef",
                  borderRadius: 8,
                  overflow: "hidden",
                  cursor: onPick ? "pointer" : "default",
                  background: "#fafafa",
                }}
                onClick={() => onPick?.(url, item)}
              >
                <Box style={{ aspectRatio: "1 / 1", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Image
                    src={url}
                    alt={item.originalName}
                    fit="contain"
                    h="100%"
                    w="100%"
                  />
                </Box>
                <Box p={6}>
                  <Tooltip label={item.originalName} withArrow>
                    <Text size="xs" truncate>
                      {item.originalName}
                    </Text>
                  </Tooltip>
                  <Group justify="space-between" mt={4}>
                    <Text size="xs" c="dimmed">
                      {(item.size / 1024).toFixed(0)} KB
                    </Text>
                    <ActionIcon
                      size="xs"
                      color="red"
                      variant="subtle"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(item._id);
                      }}
                      aria-label="Usuń"
                    >
                      ×
                    </ActionIcon>
                  </Group>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Stack>
  );
};

export default MediaGrid;
